import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { CustomerApiError, type RefundSuggestion, customerRefundsApi } from '../customer_api';
import { formatMoney } from '../lib/format';

interface Props {
  bookingId: string;
  currency?: string;
  onSuccess: () => void;
  onClose: () => void;
}

type Phase = 'loading_suggestion' | 'idle' | 'submitting' | 'success' | 'error';

/**
 * RequestRefundDrawer — allows a signed-in customer to submit a cancellation /
 * refund request against a booking. Displayed as an inline panel (drawer-style).
 *
 * Flow:
 *   1. On mount: GET /refund-suggestion → populate the suggested amount display.
 *   2. Customer writes a reason (required) and optionally toggles
 *      "Request a different amount" to enter a custom major-unit amount.
 *   3. On submit: POST /refund-request → show success state → call onSuccess.
 */
export function RequestRefundDrawer({ bookingId, currency = 'GBP', onSuccess, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('loading_suggestion');
  const [suggestion, setSuggestion] = useState<RefundSuggestion | null>(null);
  const [suggestionError, setSuggestionError] = useState(false);

  const [reason, setReason] = useState('');
  const [useDifferentAmount, setUseDifferentAmount] = useState(false);
  const [customAmountMajor, setCustomAmountMajor] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load the suggestion on mount.
  useEffect(() => {
    let cancelled = false;
    customerRefundsApi
      .getRefundSuggestion(bookingId)
      .then((s) => {
        if (cancelled) return;
        setSuggestion(s);
        setPhase('idle');
      })
      .catch(() => {
        if (cancelled) return;
        setSuggestionError(true);
        setPhase('idle');
      });
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (phase === 'submitting') return;

      const trimmedReason = reason.trim();
      if (!trimmedReason) {
        setSubmitError('Please provide a reason for your refund request.');
        return;
      }

      let requested_amount_minor: number | undefined;
      if (useDifferentAmount && customAmountMajor.trim() !== '') {
        const major = Number.parseFloat(customAmountMajor.trim());
        if (Number.isNaN(major) || major <= 0) {
          setSubmitError('Please enter a valid amount greater than zero.');
          return;
        }
        requested_amount_minor = Math.round(major * 100);
      }

      setPhase('submitting');
      setSubmitError(null);

      try {
        await customerRefundsApi.requestRefund(bookingId, {
          reason: trimmedReason,
          ...(requested_amount_minor !== undefined ? { requested_amount_minor } : {}),
        });
        setPhase('success');
        onSuccess();
      } catch (err) {
        const msg =
          err instanceof CustomerApiError
            ? err.status === 409
              ? 'A refund request is already pending for this booking.'
              : err.status === 400
                ? 'This booking does not have a payment to refund.'
                : `Request failed (HTTP ${err.status}). Please try again.`
            : 'Could not submit your request. Please try again.';
        setSubmitError(msg);
        setPhase('idle');
      }
    },
    [bookingId, phase, reason, useDifferentAmount, customAmountMajor, onSuccess],
  );

  if (phase === 'success') {
    return (
      <div
        style={{
          marginTop: '1.5rem',
          padding: '1.25rem',
          background: 'oklch(0.55 0.12 150 / 0.08)',
          borderRadius: '12px',
          border: '1px solid oklch(0.55 0.12 150 / 0.30)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: '0.9rem',
            color: 'oklch(0.36 0.12 150)',
          }}
        >
          Your refund request has been submitted. The kennel will review it shortly.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        background: 'var(--surface, white)',
        borderRadius: '12px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
      }}
      data-testid="request-refund-drawer"
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Cancel &amp; Request Refund
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={phase === 'submitting'}
          style={{
            padding: '0.25rem 0.5rem',
            borderRadius: '6px',
            border: '1px solid var(--border, oklch(0.915 0.005 80))',
            background: 'transparent',
            fontSize: '0.78rem',
            cursor: 'pointer',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Cancel
        </button>
      </div>

      {/* Suggestion */}
      {phase === 'loading_suggestion' ? (
        <p
          style={{
            margin: '0 0 1rem',
            fontSize: '0.875rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Loading policy…
        </p>
      ) : (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            background: 'var(--background, oklch(0.985 0.003 85))',
            borderRadius: '8px',
            border: '1px solid var(--border, oklch(0.915 0.005 80))',
            fontSize: '0.875rem',
          }}
        >
          {suggestionError ? (
            <span style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>
              Unable to load policy suggestion.
            </span>
          ) : suggestion ? (
            <>
              <div style={{ marginBottom: '0.25rem' }}>
                <span style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>
                  Suggested refund:{' '}
                </span>
                <span className="num" style={{ fontWeight: 500 }} data-testid="suggested-amount">
                  {formatMoney(suggestion.suggested_minor, currency)}
                </span>
                {suggestion.refund_pct_applied < 100 && (
                  <span
                    style={{
                      marginLeft: '0.5rem',
                      fontSize: '0.78rem',
                      color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                    }}
                  >
                    ({suggestion.refund_pct_applied}%)
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: '0.78rem',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                }}
              >
                Based on this kennel's cancellation policy and the time until check-in.
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate>
        {/* Reason */}
        <div style={{ marginBottom: '0.875rem' }}>
          <label
            htmlFor="refund-reason"
            style={{
              display: 'block',
              fontSize: '0.8rem',
              fontWeight: 500,
              marginBottom: '0.35rem',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
            }}
          >
            Reason <span aria-hidden="true">*</span>
          </label>
          <textarea
            id="refund-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            rows={3}
            placeholder="Please explain why you are requesting a cancellation…"
            disabled={phase === 'submitting'}
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              padding: '0.6rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'var(--surface, white)',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
            }}
          />
        </div>

        {/* Different amount toggle */}
        <div style={{ marginBottom: '0.875rem' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              cursor: 'pointer',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
            }}
          >
            <input
              type="checkbox"
              checked={useDifferentAmount}
              onChange={(e) => setUseDifferentAmount(e.target.checked)}
              disabled={phase === 'submitting'}
              data-testid="different-amount-toggle"
            />
            Request a different amount
          </label>
        </div>

        {useDifferentAmount && (
          <div style={{ marginBottom: '0.875rem' }}>
            <label
              htmlFor="refund-custom-amount"
              style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: 500,
                marginBottom: '0.35rem',
                color: 'var(--foreground, oklch(0.19 0.012 75))',
              }}
            >
              Amount ({currency.toUpperCase()})
            </label>
            <input
              id="refund-custom-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={customAmountMajor}
              onChange={(e) => setCustomAmountMajor(e.target.value)}
              disabled={phase === 'submitting'}
              placeholder="0.00"
              data-testid="custom-amount-input"
              style={{
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                padding: '0.6rem 0.75rem',
                borderRadius: '8px',
                border: '1px solid var(--border, oklch(0.915 0.005 80))',
                background: 'var(--surface, white)',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                outline: 'none',
                color: 'var(--foreground, oklch(0.19 0.012 75))',
              }}
            />
          </div>
        )}

        {submitError && (
          <p
            style={{
              margin: '0 0 0.75rem',
              fontSize: '0.875rem',
              color: 'var(--destructive, oklch(0.55 0.15 30))',
            }}
            role="alert"
          >
            {submitError}
          </p>
        )}

        <button
          type="submit"
          disabled={phase === 'submitting' || reason.trim().length === 0}
          style={{
            display: 'block',
            width: '100%',
            padding: '0.85rem 1rem',
            borderRadius: '10px',
            border: 'none',
            background: 'var(--destructive, oklch(0.55 0.15 30))',
            color: 'white',
            fontSize: '0.95rem',
            fontWeight: 500,
            cursor:
              phase === 'submitting' || reason.trim().length === 0 ? 'not-allowed' : 'pointer',
            opacity: phase === 'submitting' || reason.trim().length === 0 ? 0.6 : 1,
          }}
        >
          {phase === 'submitting' ? 'Submitting…' : 'Submit refund request'}
        </button>
      </form>
    </div>
  );
}
